import React, { useState, useEffect } from 'react';
import '.././styles/AncRegistrationForm.css';
import { useNavigate } from 'react-router-dom';
import { getBeneficiariesWoman, createPregnancy } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';
import { useSelector } from 'react-redux';

const AncRegistrationForm = () => {
  // State to hold form data
  const [formData, setFormData] = useState({
    beneficiary_id: '',
    name: '',
    age: '',
    contact_number: '',
    alternate_contact: '',
    education_level: '',
    occupation: '',
    economic_status: '',
    aadhar_number: '',
    village_id: '',
    lmp_date: '',
    registration_date: new Date().toISOString().split('T')[0],
    gravida: '',
    para: '',
    live_children: '',
    previous_miscarriages: '',
    height: '',
    weight: '',
    blood_pressure: '',
    hemoglobin: '',
    blood_group: '',
    urine_protein: '',
    urine_sugar: '',
    hiv_status: '',
    previous_complications: [],
    last_delivery_type: '',
    last_delivery_place: '',
    last_delivery_date: '',
    previous_pregnancy_notes: '',
    medical_conditions: [],
    medication: 'no',
    allergies: 'no',
    medical_history_notes: '',
    risk_status: '',
    risk_factors: [],
    risk_notes: '',
    asha_id: localStorage.getItem('asha_id') || '',
  });
  const [showUserForm, setShowUserForm] = useState(false);
  const [beneficiary, setBeneficiary] = useState(null);
  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const navigate = useNavigate();

  // Fetch beneficiaries on mount
  useEffect(() => {
    async function fetchBeneficiaries() {
      try {

        const getBen = await getBeneficiariesWoman();
        if (getBen.status === true) {
          showSuccess(getBen.message || 'Beneficiaries fetched successfully!');
          const formattedList = getBen.data.map(b => ({
            id: b.beneficiary_id,
            name: b.name,
            age: b.age,
            gender: b.gender,
            condition: b.category,
            village: b.village_name,
            village_id: b.village_id,
            contact_number: b.contact_number || '',
            aadhar_number: b.aadhar_number || '',
          }));

          setBeneficiaryList(formattedList);

          if (formattedList.length > 0) {
            const defaultBeneficiary = formattedList[0];
            setBeneficiary(defaultBeneficiary);
            setFormData((prev) => ({
              ...prev,
              beneficiary_id: defaultBeneficiary.id,
              name: defaultBeneficiary.name,
              age: defaultBeneficiary.age,
              contact_number: defaultBeneficiary.contact_number,
              aadhar_number: defaultBeneficiary.aadhar_number,
              village_id: defaultBeneficiary.village_id,
            }));
          }
        } else {
          showError(getBen.message || 'No beneficiaries found');
        }
      } catch (err) {
        showError('Network error while fetching beneficiaries');
        console.error('Fetch error:', err);
      }
    }

    fetchBeneficiaries();
  }, [selectedVillageId]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
 
  // Handle beneficiary selection
  const handleBeneficiaryChange = (e) => {
    const selected = beneficiaryList.find(b => b.name === e.target.value);
    if (selected) {
      setBeneficiary(selected);
      setFormData((prev) => ({
        ...prev,
        beneficiary_id: selected.id,
        name: selected.name,
        age: selected.age,
        contact_number: selected.contact_number,
        aadhar_number: selected.aadhar_number,
        village_id: selected.village_id,
      }));
      setShowUserForm(false);
    }
  };

  // Handle multi-select checkboxes (e.g., complications, medical conditions, risk factors)
  const handleCheckboxToggle = (field, value) => {
    setFormData((prev) => {
      const currentValues = prev[field] || [];
      if (currentValues.includes(value)) {
        return { ...prev, [field]: currentValues.filter((item) => item !== value) };
      } else {
        return { ...prev, [field]: [...currentValues, value] };
      }
    });
  };

  // Calculate EDD and gestational age
  const calculatePregnancyDetails = (lmpDate) => {
    if (!lmpDate) return { edd: '', gestationalAge: '', trimester: '' };
    const lmp = new Date(lmpDate);
    const edd = new Date(lmp);
    edd.setDate(lmp.getDate() + 280); // Approx. 40 weeks
    const today = new Date();
    const gestationalAgeWeeks = Math.floor((today - lmp) / (1000 * 60 * 60 * 24 * 7));
    const trimester = gestationalAgeWeeks <= 12 ? '1st Trimester' : gestationalAgeWeeks <= 28 ? '2nd Trimester' : '3rd Trimester';
    return {
      edd: edd.toISOString().split('T')[0],
      gestationalAge: `${gestationalAgeWeeks} weeks`,
      trimester,
    };
  };

  const { edd, gestationalAge, trimester } = calculatePregnancyDetails(formData.lmp_date);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enhanced validation
    if (!formData.beneficiary_id) {
      showError('Please select a beneficiary.');
      return;
    }
    if (!formData.lmp_date || !formData.gravida || !formData.para || !formData.weight || !formData.blood_pressure || !formData.risk_status) {
      showError('Please fill all required fields: LMP, Gravida, Para, Weight, Blood Pressure, Risk Status.');
      return;
    }

    const dataToSave = {
      beneficiary_id: parseInt(formData.beneficiary_id),
      asha_id: parseInt(formData.asha_id),
      lmp_date: formData.lmp_date,
      registration_date: formData.registration_date,
      edd,
      gravida: parseInt(formData.gravida),
      para: parseInt(formData.para),
      live_children: parseInt(formData.live_children) || 0,
      previous_miscarriages: parseInt(formData.previous_miscarriages) || 0,
      previous_complications: formData.previous_complications,
      last_delivery_type: formData.last_delivery_type,
      last_delivery_place: formData.last_delivery_place,
      last_delivery_date: formData.last_delivery_date,
      previous_pregnancy_notes: formData.previous_pregnancy_notes,
      medical_conditions: formData.medical_conditions,
      medication: formData.medication,
      allergies: formData.allergies,
      medical_history_notes: formData.medical_history_notes,
      risk_status: formData.risk_status,
      risk_factors: formData.risk_factors,
      risk_notes: formData.risk_notes,
      anc_visit_count: 1,
      anc_visit: {
        visit_number: 1,
        visit_date: formData.registration_date,
        gestational_age: parseInt(gestationalAge) || 0,
        height: parseFloat(formData.height) || null,
        weight: parseFloat(formData.weight),
        blood_pressure_systolic: parseInt(formData.blood_pressure.split('/')[0]) || 0,
        blood_pressure_diastolic: parseInt(formData.blood_pressure.split('/')[1]) || 0,
        hemoglobin: parseFloat(formData.hemoglobin) || null,
        blood_group: formData.blood_group,
        urine_protein: formData.urine_protein,
        urine_sugar: formData.urine_sugar,
        hiv_status: formData.hiv_status,
        conducted_by: parseInt(formData.asha_id),
      },
      beneficiary: {
        contact_number: formData.contact_number,
        alternate_contact: formData.alternate_contact,
        education_level: formData.education_level,
        occupation: formData.occupation,
        economic_status: formData.economic_status,
        blood_group: formData.blood_group,
        hiv_status: formData.hiv_status,
      },
    };

    if (!navigator.onLine) {
      // Save to localStorage
      const offlineData = JSON.parse(localStorage.getItem('offlineAncRegistrations') || '[]');
      offlineData.push(dataToSave);
      localStorage.setItem('offlineAncRegistrations', JSON.stringify(offlineData));
      showSuccess('Saved offline. Data will sync when online.');
      return;
    }

    try {
      const response = await createPregnancy(dataToSave);

      if (response.status) {
        showSuccess('ANC Registration saved successfully!');
        navigate('/anc'); // Redirect to dashboard
      } else {
        const errorData = await response.json();
        showError(`Failed to save ANC Registration: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving ANC Registration:', error);
      showError('An error occurred while saving.');
    }
  };

  // Sync offline data when online
  React.useEffect(() => {  
    const syncOfflineData = async () => {
      if (navigator.onLine) {
        const offlineData = JSON.parse(localStorage.getItem('offlineAncRegistrations') || '[]');
        if (offlineData.length > 0) {
          for (const data of offlineData) {
            try {
              await createPregnancy(data);         
            } catch (error) {
              console.error('Error syncing data:', error);
            }
          }
          localStorage.removeItem('offlineAncRegistrations');
          showSuccess('Offline data synced successfully!');
        }
      }
    };

    window.addEventListener('online', syncOfflineData);
    return () => window.removeEventListener('online', syncOfflineData);
  }, []);

  return (
    <>
      <div className="page-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          <span className="material-icons">arrow_back</span>
        </button>
        <div className="page-title">New ANC Registration</div>
      </div>


      <div className="progress-tracker">
        <div className="progress-step">
          <div className="step-circle completed">
            <span className="material-icons">check</span>
          </div>
          <div className="step-label">Select Beneficiary</div>
        </div>
        <div className="progress-step">
          <div className="step-circle active">1</div>
          <div className="step-label active">Pregnancy Details</div>
        </div>
        <div className="progress-step">
          <div className="step-circle">2</div>
          <div className="step-label">Medical History</div>
        </div>
        <div className="progress-step">
          <div className="step-circle">3</div>
          <div className="step-label">Care Plan</div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Beneficiary Information */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">person</span>
              Beneficiary Information
            </h2>
          </div>
          <div className="form-content">
            {/* Beneficiary Selection */}
            {beneficiary && (
              <div className="form-section">
                <div className="section-title">Select Beneficiary</div>
                <div className="beneficiary-card">
                  <div className="beneficiary-info">
                    <div className="beneficiary-name">{beneficiary.name}</div>
                    <div className="beneficiary-details">
                      {beneficiary.age} years • {beneficiary.gender} • {beneficiary.condition}
                    </div>
                    <div className="beneficiary-village">{beneficiary.village}</div>
                  </div>
                  <button
                    type="button"
                    className="change-button"
                    onClick={() => setShowUserForm(!showUserForm)}
                  >
                    Change
                  </button>
                </div>

                {showUserForm && (
                  <div className="form-section">
                    <label>Select a Beneficiary</label>
                    <select onChange={handleBeneficiaryChange} value={beneficiary.name}>
                      <option value="" disabled>Select beneficiary...</option>
                      {beneficiaryList.map((b) => (
                        <option key={b.id} value={b.name}>
                          {b.name} - {b.age} yrs - {b.village}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Age</label>
                <input
                  type="number"
                  className="text-input"
                  name="age"
                  value={formData.age}
                  disabled
                />
              </div>
              <div className="form-field">
                <label className="field-label field-required">Mobile Number</label>
                <input
                  type="tel"
                  className="text-input"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label className="field-label">Alternate Contact</label>
                <input
                  type="tel"
                  className="text-input"
                  name="alternate_contact"
                  value={formData.alternate_contact}
                  onChange={handleInputChange}
                  placeholder="Husband/Family member number"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Education Level</label>
                <select
                  className="text-input"
                  name="education_level"
                  value={formData.education_level}
                  onChange={handleInputChange}
                >
                  <option value="">Select education level</option>
                  <option value="illiterate">Illiterate</option>
                  <option value="primary">Primary Education</option>
                  <option value="secondary">Secondary Education</option>
                  <option value="higher_secondary">Higher Secondary</option>
                  <option value="graduate">Graduate</option>
                  <option value="post_graduate">Post Graduate</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Occupation</label>
                <select
                  className="text-input"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                >
                  <option value="">Select occupation</option>
                  <option value="homemaker">Homemaker</option>
                  <option value="agriculture">Agriculture</option>
                  <option value="laborer">Laborer</option>
                  <option value="service">Service</option>
                  <option value="business">Business</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Economic Status</label>
                <select
                  className="text-input"
                  name="economic_status"
                  value={formData.economic_status}
                  onChange={handleInputChange}
                >
                  <option value="">Select status</option>
                  <option value="apl">Above Poverty Line</option>
                  <option value="bpl">Below Poverty Line</option>
                  <option value="antyodaya">Antyodaya</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Pregnancy Information */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">pregnant_woman</span>
              Current Pregnancy Details
            </h2>
          </div>
          <div className="form-content">
            <div className="info-card">
              <div className="info-card-title">
                <span className="material-icons icon">info</span>
                First ANC Registration
              </div>
              <p>
                First ANC visit should ideally be within the first trimester (before
                12 weeks). Capture accurate LMP date for correct EDD calculation.
              </p>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Last Menstrual Period (LMP)</label>
                <input
                  type="date"
                  className="text-input"
                  name="lmp_date"
                  value={formData.lmp_date}
                  onChange={handleInputChange}
                />
                <div className="field-helper">First day of the last period</div>
              </div>
              <div className="form-field">
                <label className="field-label">Registration Date</label>
                <input
                  type="date"
                  className="text-input"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label className="field-label field-required">Gravida (Total Pregnancies)</label>
                <select
                  className="text-input"
                  name="gravida"
                  value={formData.gravida}
                  onChange={handleInputChange}
                >
                  <option value="1">1 (First pregnancy)</option>
                  <option value="2">2 (Second pregnancy)</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Para (Previous Deliveries)</label>
                <select
                  className="text-input"
                  name="para"
                  value={formData.para}
                  onChange={handleInputChange}
                >
                  <option value="0">0 (No previous delivery)</option>
                  <option value="1">1 (One previous delivery)</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4+</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Live Children</label>
                <input
                  type="number"
                  className="text-input"
                  name="live_children"
                  value={formData.live_children}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label className="field-label">Previous Miscarriages/Abortions</label>
                <input
                  type="number"
                  className="text-input"
                  name="previous_miscarriages"
                  value={formData.previous_miscarriages}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="lmp-calculator">
              <div className="info-card-title">
                <span className="material-icons icon">calculate</span>
                Pregnancy Calculator
              </div>
              <div className="calc-result">
                <div className="calc-result-item">
                  <div className="calc-result-label">Current Gestational Age</div>
                  <div className="calc-result-value">{gestationalAge}</div>
                </div>
                <div className="calc-result-item">
                  <div className="calc-result-label">Trimester</div>
                  <div className="calc-result-value">{trimester}</div>
                </div>
                <div className="calc-result-item">
                  <div className="calc-result-label">Estimated Delivery Date (EDD)</div>
                  <div className="calc-result-value">{edd}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Initial Assessment */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">monitor_weight</span>
              Initial Assessment
            </h2>
          </div>
          <div className="form-content">
            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Height (cm)</label>
                <input
                  type="number"
                  className="text-input"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  placeholder="Height in cm"
                />
              </div>
              <div className="form-field">
                <label className="field-label field-required">Weight (kg)</label>
                <input
                  type="number"
                  className="text-input"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="Weight in kg"
                />
              </div>
              <div className="form-field">
                <label className="field-label">BMI</label>
                <input
                  type="text"
                  className="text-input"
                  value={
                    formData.height && formData.weight
                      ? (formData.weight / ((formData.height / 100) ** 2)).toFixed(1)
                      : ''
                  }
                  disabled
                />
                <div className="field-helper">Normal range: 18.5 - 24.9</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Blood Pressure</label>
                <input
                  type="text"
                  className="text-input"
                  name="blood_pressure"
                  value={formData.blood_pressure}
                  onChange={handleInputChange}
                  placeholder="e.g., 120/80"
                />
              </div>
              <div className="form-field">
                <label className="field-label">Hemoglobin (g/dL)</label>
                <input
                  type="number"
                  className="text-input"
                  name="hemoglobin"
                  value={formData.hemoglobin}
                  onChange={handleInputChange}
                  placeholder="Hb level"
                />
                {formData.hemoglobin < 11 && (
                  <div className="field-error">Low hemoglobin level (Anemia)</div>
                )}
              </div>
              <div className="form-field">
                <label className="field-label">Blood Group</label>
                <select
                  className="text-input"
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleInputChange}
                >
                  <option value="">Select blood group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Urine Protein</label>
                <select
                  className="text-input"
                  name="urine_protein"
                  value={formData.urine_protein}
                  onChange={handleInputChange}
                >
                  <option value="">Not tested</option>
                  <option value="negative">Negative</option>
                  <option value="trace">Trace</option>
                  <option value="1+">1+</option>
                  <option value="2+">2+</option>
                  <option value="3+">3+</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Urine Sugar</label>
                <select
                  className="text-input"
                  name="urine_sugar"
                  value={formData.urine_sugar}
                  onChange={handleInputChange}
                >
                  <option value="">Not tested</option>
                  <option value="negative">Negative</option>
                  <option value="trace">Trace</option>
                  <option value="1+">1+</option>
                  <option value="2+">2+</option>
                  <option value="3+">3+</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">HIV Status</label>
                <select
                  className="text-input"
                  name="hiv_status"
                  value={formData.hiv_status}
                  onChange={handleInputChange}
                >
                  <option value="">Not tested</option>
                  <option value="negative">Negative</option>
                  <option value="positive">Positive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Obstetric History */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">history</span>
              Obstetric History
            </h2>
          </div>
          <div className="form-content">
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Previous Complications</label>
                <div className="medical-history-items">
                  {[
                    'Previous C-Section',
                    'Previous Miscarriage',
                    'Previous Stillbirth',
                    'Previous Preterm Birth',
                    'Gestational Diabetes',
                    'Pre-eclampsia',
                    'Eclampsia',
                    'Postpartum Hemorrhage',
                  ].map((complication) => (
                    <div className="medical-history-item" key={complication}>
                      <input
                        type="checkbox"
                        id={`complication-${complication}`}
                        checked={formData.previous_complications.includes(complication)}
                        onChange={() => handleCheckboxToggle('previous_complications', complication)}
                      />
                      <label htmlFor={`complication-${complication}`}>{complication}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Last Delivery Type</label>
                <select
                  className="text-input"
                  name="last_delivery_type"
                  value={formData.last_delivery_type}
                  onChange={handleInputChange}
                >
                  <option value="">Select delivery type</option>
                  <option value="normal">Normal Vaginal Delivery</option>
                  <option value="assisted">Assisted Vaginal Delivery</option>
                  <option value="cesarean">Cesarean Section</option>
                  <option value="na">Not Applicable</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Last Delivery Place</label>
                <select
                  className="text-input"
                  name="last_delivery_place"
                  value={formData.last_delivery_place}
                  onChange={handleInputChange}
                >
                  <option value="">Select place</option>
                  <option value="home">Home</option>
                  <option value="phc">Primary Health Center</option>
                  <option value="chc">Community Health Center</option>
                  <option value="dh">District Hospital</option>
                  <option value="private">Private Hospital</option>
                  <option value="other">Other</option>
                  <option value="na">Not Applicable</option>
                </select>
              </div>
              <div className="form-field">
                <label className="field-label">Last Delivery Date</label>
                <input
                  type="date"
                  className="text-input"
                  name="last_delivery_date"
                  value={formData.last_delivery_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Notes on Previous Pregnancy</label>
                <textarea
                  className="text-input"
                  name="previous_pregnancy_notes"
                  value={formData.previous_pregnancy_notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add any relevant notes about previous pregnancy"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Medical History */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">medical_services</span>
              Medical History
            </h2>
          </div>
          <div className="form-content">
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Pre-existing Medical Conditions</label>
                <div className="medical-history-items">
                  {[
                    'Diabetes',
                    'Hypertension',
                    'Cardiac Disease',
                    'Anemia',
                    'Thyroid Disorder',
                    'Tuberculosis',
                    'HIV/AIDS',
                    'Epilepsy',
                  ].map((condition) => (
                    <div className="medical-history-item" key={condition}>
                      <input
                        type="checkbox"
                        id={`condition-${condition}`}
                        checked={formData.medical_conditions.includes(condition)}
                        onChange={() => handleCheckboxToggle('medical_conditions', condition)}
                      />
                      <label htmlFor={`condition-${condition}`}>{condition}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Currently Taking Medication</label>
                <div className="radio-group">
                  <div className="radio-option">
                    <input
                      type="radio"
                      name="medication"
                      id="medication-yes"
                      value="yes"
                      checked={formData.medication === 'yes'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="medication-yes">Yes</label>
                  </div>
                  <div className="radio-option">
                    <input
                      type="radio"
                      name="medication"
                      id="medication-no"
                      value="no"
                      checked={formData.medication === 'no'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="medication-no">No</label>
                  </div>
                </div>
              </div>
              <div className="form-field">
                <label className="field-label">Allergies</label>
                <div className="radio-group">
                  <div className="radio-option">
                    <input
                      type="radio"
                      name="allergies"
                      id="allergies-yes"
                      value="yes"
                      checked={formData.allergies === 'yes'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="allergies-yes">Yes</label>
                  </div>
                  <div className="radio-option">
                    <input
                      type="radio"
                      name="allergies"
                      id="allergies-no"
                      value="no"
                      checked={formData.allergies === 'no'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="allergies-no">No</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Additional Medical History Notes</label>
                <textarea
                  className="text-input"
                  name="medical_history_notes"
                  value={formData.medical_history_notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add any additional relevant medical history information"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Risk Assessment */}
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-icons icon">warning</span>
              Risk Assessment
            </h2>
          </div>
          <div className="form-content">
            <div className="form-row">
              <div className="form-field">
                <label className="field-label field-required">Risk Status</label>
                <select
                  className="text-input"
                  name="risk_status"
                  value={formData.risk_status}
                  onChange={handleInputChange}
                >
                  <option value="normal">Normal</option>
                  <option value="at_risk">At Risk</option>
                  <option value="high_risk">High Risk</option>
                </select>
              </div>
              <div className="form-field full-width">
                <label className="field-label">Identified Risk Factors</label>
                <div className="medical-history-items">
                  {[
                    'Anemia (Hb < 11)',
                    'Previous Miscarriage',
                    'Hypertension',
                    'Diabetes',
                    'Pre-eclampsia',
                    'Multiple Pregnancy',
                    'Age < 18 or > 35',
                    'Low BMI',
                  ].map((factor) => (
                    <div className="medical-history-item" key={factor}>
                      <input
                        type="checkbox"
                        id={`risk-factor-${factor}`}
                        checked={formData.risk_factors.includes(factor)}
                        onChange={() => handleCheckboxToggle('risk_factors', factor)}
                      />
                      <label htmlFor={`risk-factor-${factor}`}>{factor}</label>
                    </div>
                  ))}
                </div>
                <div>
                  {formData.risk_factors.map((factor) => (
                    <span key={factor} className="risk-tag">{factor}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Notes on Risk Factors</label>
                <textarea
                  className="text-input"
                  name="risk_notes"
                  value={formData.risk_notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add notes about identified risk factors"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            type="button"
            className="button secondary"
            onClick={() => navigate('/dashboard')}
          >
            <span className="material-icons icon">close</span>
            Cancel
          </button>
          <button type="submit" className="button primary">
            <span className="material-icons icon">save</span>
            Save & Continue
          </button>
        </div>
      </form>
    </>
  );
};

export default AncRegistrationForm;